import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import {
  map,
} from 'rxjs/operators';
import { of as ObservableOf } from 'rxjs/observable/of';
import { isNil as _isNil } from 'lodash';

import { BlApiClient, BlApiParams, BlConditionType } from '@blcloud/bl-ng-common';
import {
  IDmContentInfo,
  IDmIssueSetting,
  IDmApproachInfo,
  IDmIssueInfo,
  IDmApproachInfoBulkUpdate,
  ISnsShopAccountSetting,
  IOrganizationInformation,
} from '@blcloud/bl-datamodel';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { Rules } from '@blcloud/bl-ng-ui-component';
import {
  LoginResourceService,
  OrganizationInformationResourceService,
} from '@blcloud/bl-ng-share-module';
import {
  DmContentInfoResource,
  DmIssueSettingResource,
  DmApproachInfoResource,
  DmIssueInfoResource,
  DmApproachInfoBulkUpdateResource,
  SnsShopAccountSettingResource,
} from '@blcloud/bl-ng-resource';
import { RoleConst } from '@blcloud/bl-datamodel/const/role';
import { ContractConst } from '@blcloud/bl-datamodel/const/contract';

/**
 * 送信確認モーダル サービス
 */
@Injectable()
export class SnsSendMessageModalService {

  /** コンストラクタ */
  constructor(
    private dmContentInfoResource: DmContentInfoResource,
    private dmIssueSettingResource: DmIssueSettingResource,
    private dmApproachInfoResource: DmApproachInfoResource,
    private dmIssueInfoResource: DmIssueInfoResource,
    private loginResourceService: LoginResourceService,
    private organizationResource: OrganizationInformationResourceService,
    private dmApproachInfoBulkUpdateResource: DmApproachInfoBulkUpdateResource,
    private snsShopAccountSettingResource: SnsShopAccountSettingResource,
    private apiClient: BlApiClient,
  ) {
  }

  /**
   * 組織情報を取得します。
   *
   * @returns 組織情報
   */
  getOrganizationInfo(organizationCode): Observable<IOrganizationInformation> {
    return this.organizationResource.getCurrentOrganization(organizationCode);
  }

  /**
   * 案内文情報取得
   * @param dmExtractMenuCode DM抽出メニューコード
   * @param dmInvitationMediaDiv DM案内媒体区分
   * @return 案内文情報
   */
  dmContentSearch(dmExtractMenuCode, dmInvitationMediaDiv): Observable<IDmContentInfo[]> {
    const conditions = [
      { key: 'dmExtractMenuCode', value: dmExtractMenuCode, type: BlConditionType.In },
      { key: 'dmInvitationMediaDiv', value: dmInvitationMediaDiv, type: BlConditionType.Equal },
      { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal },
    ];

    const sortFields = [{ field: 'updateDateTime', desc: true }];

    return this.dmContentInfoResource.search(
      { conditions, sortFields, size: 100 }
    ).pipe(
      map(res => res ? res.searchResultList : [])
    );
  }

  /**
   * DM発行設定情報取得
   * @param organizationCode 組織コード
   * @return DM発行設定情報
   */
  dmIssueSearch(organizationCode): Observable<IDmIssueSetting> {
    return this.dmIssueSettingResource.getById(organizationCode);
  }

  /**
   * アプローチ情報の更新
   * @param items アプローチ情報
   */
  approachUpdateAll(items: IDmApproachInfo[]) {
    return this.dmApproachInfoResource.put(items);
  }

  /**
   * DM発行設定情報のバリデーションルール取得
   * @returns バリデーションルール
   */
  getDmIssueSettingRules(): Observable<Rules[]> {
    return this.dmIssueSettingResource.getRules();
  }

  /**
   * DM案内文設定情報のバリデーションルール取得
   * @returns バリデーションルール
   */
  getDmContentInfoRules(): Observable<Rules[]> {
    return this.dmContentInfoResource.getRules({ params: { r: '1' } });
  }

  /**
   * 一括更新
   * @param param アプローチ情報
   * @return 更新結果
   */
  putAllDate(param, option): Observable<any> {
    return this.dmApproachInfoBulkUpdateResource.post<IDmApproachInfoBulkUpdate>(param, option);
  }


  /**
   * DM発行情報登録
   * @param param DM案内媒体区分
   * @return 登録結果
   */
  postIssueInfo(param, option): Observable<any> {
    return this.dmIssueInfoResource.post<IDmIssueInfo>(param, option);
  }

  /**
   * ログインユーザー情報取得
   * @return ログインユーザー情報
   */
  getLoginUserInfo(useCache = true) {
    return this.loginResourceService.getLoginUserEmployeeBindModel(useCache);
  }

  /**
   * SNS店舗アカウント設定情報取得
   * @return SNS店舗アカウント設定情報リスト
   */
  getSnsShopAccountSetting(): Observable<ISnsShopAccountSetting[]> {
    const conditions = [
      { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal },
    ];
    return this.snsShopAccountSettingResource.getAll<ISnsShopAccountSetting>({
      conditions,
    }).pipe(
      map(response => response.searchResultList),
    );
  }

  /**
   * SMS送信のロールを取得
   */
  isAvailableSmsSendRole(): Observable<boolean> {
    return this.loginResourceService.isAvailableFunction(RoleConst.ROLEID_FUNC_ALL_SMS_SEND);
  }

  /**
   * メール送信のロールを取得
   */
  isAvailableMailSendRole(): Observable<boolean> {
    return this.loginResourceService.isAvailableFunction(RoleConst.ROLEID_FUNC_ALL_MAIL_SEND);
  }

  /**
   * クーポン管理サービスOP有効判定
   */
  isAvailableCouponMangeService(): Observable<boolean> {
    return this.loginResourceService.isAvailableService(ContractConst.SERVICEID_OPT_CMN_COUPON_MANAGE);
  }

  /**
   * Web予約クーポン管理サービスOP有効判定
   */
  isAvailableWebReserveService(): Observable<boolean> {
    return this.loginResourceService.isAvailableService(ContractConst.SERVICEID_OPT_CMN_WEB_RESERVE);
  }

  /**
   * 画像ファイルの公開領域へのアップロードを行います。
   * @param file アップロードするファイル
   * @param snslinkageid SNS連携ID
   */
  postImage(file: File, snslinkageid: string): Observable<any> {
    if (_isNil(file)) {
      return ObservableOf(null);
    }
    // エンティティ定義
    const apiParams: BlApiParams = {
      subDomain: 'file',
      entityId: 'imageuploadinfo',
      subject: 'all',
      exPath: 'snsimageupload'
    };

    // フォームデータ作成
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('linkageid', snslinkageid);

    return this.apiClient.request('post', apiParams, {
      body: formData,
      observe: 'response',
      responseType: 'json',
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/sns-send-message-modal/sns-send-message-modal.service.ts